import { useEffect, useRef } from "react";
import {
  FORM_1120_REVIEW_STATUS,
  ReviewNotes as ReviewNotesType,
  ReviewIssue,
  ReviewSection,
  ReviewDataQuality,
  useGetForm1120ReviewQuery,
} from "store/apis/Task1120Tracker";
import { Button } from "components/DesignSystem/Button/Button";

const RISK_STYLES: Record<string, string> = {
  Low: "t-bg-green-0 t-text-green t-border-green-20",
  Medium: "t-bg-yellow-0 t-text-yellow-60 t-border-yellow-20",
  High: "t-bg-red-0 t-text-red t-border-red-20",
};

function SectionCard({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="t-rounded-lg t-border t-border-solid t-border-neutral-10 t-overflow-hidden">
      <div className="t-bg-surface-lighter-grey t-px-4 t-py-2 t-border-b t-border-solid t-border-neutral-10">
        <span className="t-text-overline t-text-text-30">{label}</span>
      </div>
      <div className="t-flex t-flex-col t-divide-y t-divide-solid t-divide-neutral-10">
        {children}
      </div>
    </div>
  );
}

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="t-flex t-flex-col t-gap-0.5">
      <span className="t-text-body-xs t-font-semibold t-text-text-30">
        {label}
      </span>
      <span className="t-text-body-xs t-text-text-80 t-leading-relaxed">
        {value}
      </span>
    </div>
  );
}

function IssueCard({ item }: { item: ReviewIssue }) {
  return (
    <div className="t-px-4 t-py-3 t-flex t-flex-col t-gap-2">
      <div className="t-text-body-sm t-font-semibold t-text-text-100">
        {item.issue}
      </div>
      <FieldRow label="Observation" value={item.observation} />
      <FieldRow label="Impact" value={item.impact} />
      <FieldRow label="Action" value={item.action} />
    </div>
  );
}

function SectionItemCard({ item }: { item: ReviewSection }) {
  return (
    <div className="t-px-4 t-py-3 t-flex t-flex-col t-gap-2">
      <div className="t-text-body-sm t-font-semibold t-text-text-100">
        {item.area}
      </div>
      <FieldRow label="Observation" value={item.observation} />
      <FieldRow label="Risk" value={item.risk} />
      <FieldRow label="Action" value={item.action} />
    </div>
  );
}

function DataQualityCard({ item }: { item: ReviewDataQuality }) {
  return (
    <div className="t-px-4 t-py-3 t-flex t-flex-col t-gap-2">
      <div className="t-text-body-sm t-font-semibold t-text-text-100">
        {item.issue}
      </div>
      <FieldRow label="Details" value={item.details} />
      <FieldRow label="Action" value={item.action} />
    </div>
  );
}

function ReviewNotesPanel({ notes }: { notes: ReviewNotesType }) {
  const { overall_assessment: oa } = notes;
  const riskStyle = RISK_STYLES[oa.risk_level] ?? RISK_STYLES.Medium;

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <div
        className={`t-rounded-lg t-border t-border-solid t-p-4 t-flex t-flex-col t-gap-2 ${riskStyle}`}
      >
        <div className="t-flex t-items-center t-justify-between t-gap-3">
          <span className="t-text-body-sm t-font-semibold">{notes.title}</span>
          <span
            className={`t-text-body-xs t-font-semibold t-px-2 t-py-0.5 t-rounded-full t-border t-border-solid ${riskStyle}`}
          >
            {oa.risk_level} Risk
          </span>
        </div>
        <p className="t-text-body-xs t-leading-relaxed t-m-0">
          {oa.main_concerns}
        </p>
        <div className="t-text-body-xs t-leading-relaxed t-whitespace-pre-line">
          {Array.isArray(oa.recommended_next_steps)
            ? oa.recommended_next_steps.map((step, i) => (
                <div key={i} className="t-flex t-gap-2 t-items-start">
                  <span className="t-flex-shrink-0">•</span>
                  <span>{step}</span>
                </div>
              ))
            : oa.recommended_next_steps}
        </div>
      </div>

      {notes.key_issues.length > 0 && (
        <SectionCard label={`KEY ISSUES (${notes.key_issues.length})`}>
          {notes.key_issues.map((item, i) => (
            <IssueCard key={i} item={item} />
          ))}
        </SectionCard>
      )}

      {notes.financial_review.length > 0 && (
        <SectionCard label="FINANCIAL REVIEW">
          {notes.financial_review.map((item, i) => (
            <SectionItemCard key={i} item={item} />
          ))}
        </SectionCard>
      )}

      {notes.compliance_operational_review.length > 0 && (
        <SectionCard label="COMPLIANCE / OPERATIONAL">
          {notes.compliance_operational_review.map((item, i) => (
            <SectionItemCard key={i} item={item} />
          ))}
        </SectionCard>
      )}

      {notes.data_quality_integrity_checks.length > 0 && (
        <SectionCard label="DATA QUALITY / INTEGRITY">
          {notes.data_quality_integrity_checks.map((item, i) => (
            <DataQualityCard key={i} item={item} />
          ))}
        </SectionCard>
      )}

      {notes.key_questions_for_reviewer.length > 0 && (
        <SectionCard label="KEY QUESTIONS FOR REVIEWER">
          <div className="t-px-4 t-py-3 t-flex t-flex-col t-gap-2">
            {notes.key_questions_for_reviewer.map((q, i) => (
              <div key={i} className="t-flex t-gap-2 t-items-start">
                <span className="t-text-body-xs t-text-purple t-font-semibold t-flex-shrink-0">
                  {i + 1}.
                </span>
                <span className="t-text-body-xs t-text-text-80 t-leading-relaxed">
                  {q}
                </span>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}

export const Task1120AgentProcessing = ({
  reviewId,
  onRerun,
}: {
  reviewId: string;
  onRerun: () => void;
}) => {
  const isTerminal = useRef(false);

  const { data: review } = useGetForm1120ReviewQuery(
    { review_id: reviewId },
    {
      pollingInterval: isTerminal.current ? 0 : 10000,
      skip: !reviewId,
    }
  );

  useEffect(() => {
    if (
      review?.status === FORM_1120_REVIEW_STATUS.COMPLETED ||
      review?.status === FORM_1120_REVIEW_STATUS.FAILED
    ) {
      isTerminal.current = true;
    }
  }, [review?.status]);

  const reviewNotes =
    review?.result &&
    typeof review.result === "object" &&
    "review_notes" in review.result
      ? (review.result as { review_notes: ReviewNotesType }).review_notes
      : null;

  const isInProgress =
    !review?.status ||
    review.status === FORM_1120_REVIEW_STATUS.PENDING ||
    review.status === FORM_1120_REVIEW_STATUS.PROCESSING;

  return (
    <div className="t-flex t-flex-col t-gap-4 t-p-5 t-h-full t-overflow-y-auto">
      {isInProgress && (
        <div className="t-flex t-flex-col t-items-center t-justify-center t-gap-3 t-py-16 t-text-text-30">
          <div className="t-w-8 t-h-8 t-rounded-full t-border-2 t-border-solid t-border-purple t-border-t-transparent t-animate-spin" />
          <span className="t-text-body-sm">Review in progress…</span>
          <span className="t-text-body-xs t-text-text-20">
            You will be notified on Slack when complete.
          </span>
        </div>
      )}

      {review?.status === FORM_1120_REVIEW_STATUS.COMPLETED && reviewNotes && (
        <ReviewNotesPanel notes={reviewNotes} />
      )}

      {review?.status === FORM_1120_REVIEW_STATUS.FAILED && (
        <div className="t-rounded-lg t-border t-border-solid t-border-red-20 t-bg-red-0 t-p-4 t-text-body-sm t-text-red">
          Review failed:{" "}
          {review.error_message || "An unexpected error occurred."}
        </div>
      )}

      {(review?.status === FORM_1120_REVIEW_STATUS.COMPLETED ||
        review?.status === FORM_1120_REVIEW_STATUS.FAILED) && (
        <Button customType="secondary" size="small" onClick={onRerun}>
          Rerun Analysis
        </Button>
      )}
    </div>
  );
};
